import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {EMPTY} from 'rxjs';
import {PostProcessingStep, PostProcessingStepType, SelectorAndPage, SelectorResult} from '../model';
import {environment} from '../../environments/environment';

@Component({
  selector: 'app-post-processing-editor',
  templateUrl: './post-processing-editor.component.html',
  styleUrls: ['./post-processing-editor.component.css'],
  standalone: false,
})
export class PostProcessingEditorComponent implements OnInit, OnDestroy {
  @Input() sel!: SelectorResult;
  @Input() pageSource: string = '';
  @Input() pageSource2: string = '';
  @Input() token: string = '';
  /** Field path (e.g. 'prices.price'), needed to preview a schema step with the field's default lookup. */
  @Input() field: string | null = null;

  preview1Processed: string | null = null;
  preview2Raw: string | null = null;
  preview2Processed: string | null = null;
  previewPending = 0;
  valuesInputs: (string | undefined)[] = [];

  private previewTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private http: HttpClient) {}

  /** Initializes post_processing to an empty array if the input selector has none. */
  ngOnInit(): void {
    if (!this.sel.post_processing) {
      this.sel.post_processing = [];
    }
  }

  /** Cancels any pending preview timer on destroy to avoid stale HTTP calls. */
  ngOnDestroy(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  }

  /** Alias for sel.post_processing for template convenience. */
  get steps(): PostProcessingStep[] {
    return this.sel.post_processing;
  }

  /** Appends a default replace step and schedules a preview refresh. */
  addStep(): void {
    this.steps.push({type: 'replace', old: '', new: ''});
    this.schedulePreview();
  }

  /** Removes the step and its valuesInputs entry at index i, then schedules a preview refresh. */
  removeStep(i: number): void {
    this.steps.splice(i, 1);
    this.valuesInputs.splice(i, 1);
    this.schedulePreview();
  }

  /** Swaps step i with the step above it; no-op at index 0. */
  moveUp(i: number): void {
    if (i === 0) return;
    [this.steps[i - 1], this.steps[i]] = [this.steps[i], this.steps[i - 1]];
    [this.valuesInputs[i - 1], this.valuesInputs[i]] = [this.valuesInputs[i], this.valuesInputs[i - 1]];
    this.schedulePreview();
  }

  /** Swaps step i with the step below it; no-op at the last index. */
  moveDown(i: number): void {
    if (i === this.steps.length - 1) return;
    [this.steps[i], this.steps[i + 1]] = [this.steps[i + 1], this.steps[i]];
    [this.valuesInputs[i], this.valuesInputs[i + 1]] = [this.valuesInputs[i + 1], this.valuesInputs[i]];
    this.schedulePreview();
  }

  /** Changes a step's type, clears all type-specific fields, and sets the defaults for the new type. */
  onTypeChange(step: PostProcessingStep, newType: PostProcessingStepType): void {
    step.type = newType;
    step.pattern = undefined;
    step.group = undefined;
    step.old = undefined;
    step.new = undefined;
    step.values = undefined;
    step.prefix = undefined;
    step.suffix = undefined;
    if (newType === 'regex_extract') {
      step.pattern = '';
      step.group = 1;
    } else if (newType === 'replace') {
      step.old = '';
      step.new = '';
    } else if (newType === 'map_none_if') {
      step.values = [];
    } else if (newType === 'strip_affix') {
      step.prefix = '';
      step.suffix = '';
    }
    this.schedulePreview();
  }

  /** Returns a human-readable warning if the step is misconfigured, or null when valid. */
  getWarning(step: PostProcessingStep): string | null {
    if (step.type === 'regex_extract' && !step.pattern) {
      return 'Pattern is required.';
    }
    if (step.type === 'strip_affix' && !step.prefix && !step.suffix) {
      return 'Specify at least a prefix or suffix.';
    }
    if (step.type === 'map_none_if' && (!step.values || step.values.length === 0)) {
      return 'Add at least one sentinel value.';
    }
    return null;
  }

  /** Trims input, adds it to step.values, and clears the input field at idx; no-op for whitespace-only input. */
  addValue(step: PostProcessingStep, input: string, idx: number): void {
    const val = (input ?? '').trim();
    if (!val) return;
    if (!step.values) step.values = [];
    step.values.push(val);
    this.valuesInputs[idx] = '';
    this.schedulePreview();
  }

  /** Removes the value at index vi from step.values and schedules a preview refresh. */
  removeValue(step: PostProcessingStep, vi: number): void {
    if (!step.values) return;
    step.values.splice(vi, 1);
    this.schedulePreview();
  }

  /** Submits addValue when Enter is pressed inside a map_none_if value input. */
  onValuesKeydown(event: KeyboardEvent, step: PostProcessingStep, input: string, idx: number): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addValue(step, input, idx);
    }
  }

  /** Debounces preview execution by 400 ms; resets the timer on each call so only the final change triggers a request. */
  schedulePreview(): void {
    if (this.previewTimer) clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.runPreview(), 400);
  }

  /** Returns the extracted string, or null when the response signals a failed extraction. */
  private parseResult(res: unknown): string | null {
    const s = res as string;
    return s && !s.startsWith('Could not extract') ? s : null;
  }

  /** Builds and sends a POST to /extract_value with the current selector, page source, and step list. */
  private post(pageSource: string | null, extractedValue: string | null, steps: PostProcessingStep[]) {
    const payload: SelectorAndPage = {
      selector: this.sel.selector,
      value_to_extract: this.sel.value_to_extract ?? null,
      page_source: pageSource,
      extracted_value: extractedValue,
      post_processing: steps,
      is_schema: this.sel.is_schema ?? false,
      schema_paths: this.sel.schema_paths ?? [],
      field: this.field,
    };
    const headers = new HttpHeaders({'auth': this.token});
    return this.http.post<string>(environment.apiUrl + '/extract_value', payload, {headers});
  }

  /** Fires immediately after the debounce; sends 1 request when only extracted_value is available, or 3 when pageSource2 is set. */
  private runPreview(): void {
    if (!this.sel.extracted_value && !this.pageSource) {
      return;
    }
    this.previewPending = this.pageSource2 ? 3 : 1;

    this.post(this.pageSource || null, this.sel.extracted_value ?? null, this.steps)
      .pipe(catchError(() => { this.previewPending--; return EMPTY; }))
      .subscribe(res => { this.previewPending--; this.preview1Processed = this.parseResult(res); });

    if (this.pageSource2) {
      this.post(this.pageSource2, null, [])
        .pipe(catchError(() => { this.previewPending--; return EMPTY; }))
        .subscribe(res => { this.previewPending--; this.preview2Raw = this.parseResult(res); });

      this.post(this.pageSource2, null, this.steps)
        .pipe(catchError(() => { this.previewPending--; return EMPTY; }))
        .subscribe(res => { this.previewPending--; this.preview2Processed = this.parseResult(res); });
    }
  }

  /** Returns the tooltip description string for the given step type. */
  tooltipFor(type: PostProcessingStepType): string {
    const tooltips: Record<PostProcessingStepType, string> = {
      regex_extract: 'Searches the value for the regex pattern. Returns capture group (0 = whole match). If not found, the selector is treated as having no result.',
      replace: 'Replaces all occurrences of "find" with "replace with". Useful for stripping currency symbols or normalising separators.',
      map_none_if: 'If the trimmed value equals any entry in the list, the field is treated as empty. Use for sentinel strings like "-", "N/A", "Preis auf Anfrage".',
      strip_affix: 'Removes a known constant prefix from the start and/or suffix from the end. Useful when a label is always part of the text node.',
    };
    return tooltips[type] ?? '';
  }
}
